import type { RootState } from "../../../store.ts"
import type { CalendarPermissions, Event } from "../types/types.ts"

type Permissions = RootState["auth"]["permissions"]

/** Whether the signed-in user can edit calendar events directly — the single
 *  source every caller reads (the Calendar page's own gate, and any
 *  deep-linking caller such as the Lead Workspace's appointment Edit
 *  action), so the two can't diverge. */
export const calendarEditAllowedFromPermissions = (
  permissions: Permissions | undefined
): boolean => !!permissions?.codes?.calendar?.calendar?.calendar_edit

export const selectCalendarEditAllowed = (state: RootState): boolean =>
  calendarEditAllowedFromPermissions(state.auth?.permissions)

/** Whether the signed-in user is this event's own creator — the one place
 *  reminder ownership is decided (`canActOnReminder` below reads it, rather
 *  than re-deriving `userId === event.created_by.id` independently). */
export const isOwnCalendarEvent = (
  event: Pick<Event, "created_by"> | undefined,
  userId: string | undefined
): boolean => userId === event?.created_by?.id

/** Whether this reminder's Edit/Delete/Mark-Complete actions are offered to
 *  the signed-in user, ownership-wise (`canEditCalendarEvent` and
 *  `CalendarViewReminder`'s `isView` both read it) — a reminder linked to a
 *  lead/project is shared team-owned, gated by that record's own edit
 *  permission already, not personal to whoever happened to create it. */
export const canActOnReminder = (
  event: Pick<Event, "created_by" | "lead" | "project"> | undefined,
  userId: string | undefined
): boolean =>
  isOwnCalendarEvent(event, userId) || !!event?.lead || !!event?.project

/** The one rule for whether this event's Edit action is offered at all —
 *  edit permission, plus ownership for a reminder. */
export const canEditCalendarEvent = (
  permissions: CalendarPermissions | undefined,
  event: Pick<Event, "type" | "created_by" | "lead" | "project"> | undefined,
  userId: string | undefined
): boolean => {
  const isReminder = event?.type?.toLowerCase() === "reminder"
  return !!permissions?.edit && (!isReminder || canActOnReminder(event, userId))
}

/** Resolves an appointment/follow-up's Edit action to the linked event's id
 *  when eligible for the in-place editor (`StandaloneEventEditModal`), or
 *  `null` to fall back to the caller's own scoped editor — shared by all 3
 *  call sites so a future change applies to every one of them. An
 *  early-return guard rather than a boolean predicate plus a cast, so
 *  TypeScript narrows `eventId` to the return type itself. No `date` check:
 *  that was this function's own pre-deep-link-removal leftover — the event
 *  is fetched fresh by id, so a lead/row's denormalized date field being
 *  null or stale is no reason to fall back to the more limited editor. */
export const resolveInPlaceEditEventId = (
  canEditLead: boolean | undefined,
  calendarEditAllowed: boolean,
  eventId: string | null | undefined
): string | null => {
  if (!canEditLead || !calendarEditAllowed || !eventId) return null
  return eventId
}
